import React, { useEffect, useState } from 'react'
import { Gamepad2, Loader2 } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import formula1Service from '../services/formula1Service'
import { Formula1Race } from '../types/formula1'
import { isFormula1RaceUpcoming, pickUpcomingFormula1Races } from '../utils/formula1RaceTiming'

interface Formula1GamesAndPredictionsModalProps {
  roomId: string
  isOpen: boolean
  onClose: () => void
  onSelectRace: (race: Formula1Race) => void
  mode?: 'all' | 'upcoming' | 'played'
}

const Formula1GamesAndPredictionsModal: React.FC<Formula1GamesAndPredictionsModalProps> = ({
  roomId,
  isOpen,
  onClose,
  onSelectRace,
  mode = 'all',
}) => {
  const { t } = useTranslation()
  const [races, setRaces] = useState<Formula1Race[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !roomId) return
    let cancelled = false
    setLoading(true)
    formula1Service
      .getRaces({ limit: 50 })
      .then((data) => {
        if (!cancelled) {
          const list = Array.isArray(data) ? data : []
          const sorted = [...list].sort((a, b) => (a.day ?? 0) - (b.day ?? 0))
          const filtered =
            mode === 'played'
              ? sorted.filter((r) => r.status === 'completed' || !isFormula1RaceUpcoming(r))
              : mode === 'upcoming'
                ? pickUpcomingFormula1Races(sorted)
                : sorted
          setRaces(filtered)
        }
      })
      .catch(() => {
        if (!cancelled) setRaces([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [isOpen, roomId, mode])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] max-w-6xl h-[85vh] sm:h-[82vh] md:h-[80vh] max-h-[80vh] overflow-hidden flex flex-col glass-strong rounded-xl sm:rounded-2xl min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10 bg-white/5">
          <h2 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/20 text-primary">
              <Gamepad2 className="w-5 h-5" />
            </span>
            {mode === 'played'
              ? t('roomPage.played')
              : mode === 'upcoming'
                ? t('formula1.upcomingRaces')
                : t('formula1.racesAndPredictions')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('stats.close')}
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : races.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm sm:text-base">
              {t('formula1.noRacesAvailable')}
            </div>
          ) : (
            <ul className="space-y-3">
              {races.map((race) => (
                <li key={race.id ?? race._id}>
                  <button
                    type="button"
                    onClick={() => onSelectRace(race)}
                    className="w-full text-left rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all border bg-card hover:bg-muted/50 border-border/50 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <div className="font-bold text-foreground text-base sm:text-lg tracking-tight">
                      🏎️ Race {race.day}: {race.city}{race.country ? `, ${race.country}` : ''}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-muted-foreground">
                      {race.raceDate && (
                        <span>{new Date(race.raceDate).toLocaleDateString()}</span>
                      )}
                      {Array.isArray(race.drivers) && (
                        <span>{race.drivers.length} drivers</span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        race.status === 'completed' ? 'bg-success/20 text-success' : race.status === 'in-progress' ? 'bg-warning/20 text-warning' : 'bg-primary/20 text-primary'
                      }`}>
                        {race.status || 'upcoming'}
                      </span>
                      {race.actualResults && Object.keys(race.actualResults).length > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-primary/15 text-primary font-semibold">
                          {t('formula1.resultsAvailable')}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-white/10 bg-white/5">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Formula1GamesAndPredictionsModal
